/*!
 * Swiss Emporium - in-stock products first, sold-out products last.
 *
 * Shopify paginates collections / search results on the server, so Liquid can only
 * re-order the products that are on the current page. To guarantee that no sold-out
 * product ever appears above an in-stock one *across pages*, this script:
 *   1. reads every page of the current listing (same URL, same sort, same filters),
 *   2. stable-partitions all cards: available first, sold out after (the existing
 *      sort order is preserved inside each group),
 *   3. shows the slice that belongs to the page the visitor asked for.
 * Page size, pagination links and the "Showing x-y of z" text stay exactly as the
 * server rendered them, so nothing else in the theme needs to change.
 *
 * Markup contract (see snippets/vela-collection-grid|list.liquid, templates/search.liquid):
 *   [data-se-stock-list]  data-se-pages, data-se-page, data-se-size
 *   > first child (row)   > .velaProBlock[data-available="true|false"]
 */
(function () {
  'use strict';

  var LIST_SEL = '[data-se-stock-list]';
  var FAILSAFE_MS = 6000;
  var CONCURRENCY = 4;
  var cache = {};   // listing key -> { pageNumber: [cardHtml, ...] }
  var runId = 0;

  function params() {
    return new URLSearchParams(window.location.search);
  }

  function listingKey() {
    var p = params();
    p['delete']('page');
    if (p.sort) { p.sort(); }
    return window.location.pathname + '?' + p.toString();
  }

  function pageUrl(n) {
    var p = params();
    p.set('page', n);
    return window.location.pathname + '?' + p.toString();
  }

  function rowOf(list) {
    return list.firstElementChild;
  }

  function cardsIn(list) {
    var row = rowOf(list);
    if (!row) { return []; }
    return Array.prototype.filter.call(row.children, function (el) {
      return el.classList && el.classList.contains('velaProBlock');
    });
  }

  function isAvailable(card) {
    return card.getAttribute('data-available') !== 'false';
  }

  function cardId(card) {
    var a = card.querySelector('.proName a, a.proFeaturedImage');
    return a ? a.getAttribute('href') : card.outerHTML.length + ':' + card.textContent.slice(0, 60);
  }

  function release(list) {
    if (list && list.getAttribute('data-se-stock') === 'pending') {
      list.removeAttribute('data-se-stock');
    }
  }

  function fetchPage(n) {
    return fetch(pageUrl(n), { credentials: 'same-origin', headers: { 'X-Requested-With': 'se-instock-first' } })
      .then(function (res) {
        if (!res.ok) { throw new Error('HTTP ' + res.status); }
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var list = doc.querySelector(LIST_SEL);
        if (!list) { throw new Error('no list in page ' + n); }
        return cardsIn(list).map(function (c) { return c.outerHTML; });
      });
  }

  // Fetch the pages we do not have yet, a few at a time.
  function loadMissing(store, pages) {
    var queue = [];
    for (var n = 1; n <= pages; n++) { if (!store[n]) { queue.push(n); } }
    var failed = false;

    function worker() {
      if (failed || !queue.length) { return Promise.resolve(); }
      var n = queue.shift();
      return fetchPage(n).then(function (cards) {
        store[n] = cards;
        return worker();
      }, function (err) {
        failed = true;
        throw err;
      });
    }

    var workers = [];
    for (var i = 0; i < CONCURRENCY; i++) { workers.push(worker()); }
    return Promise.all(workers);
  }

  function htmlToCard(html) {
    var tpl = document.createElement('template');
    tpl.innerHTML = html.trim();
    return tpl.content.firstElementChild;
  }

  function run() {
    var list = document.querySelector(LIST_SEL);
    if (!list || list.getAttribute('data-se-stock-done')) { return; }

    var pages = parseInt(list.getAttribute('data-se-pages'), 10) || 1;
    var current = parseInt(list.getAttribute('data-se-page'), 10) || 1;
    var size = parseInt(list.getAttribute('data-se-size'), 10) || 0;
    if (pages < 2 || !size || !window.fetch || !window.URLSearchParams || !window.DOMParser) {
      release(list);
      return;
    }

    var token = ++runId;
    var key = listingKey();
    var store = cache[key] || (cache[key] = {});
    var liveCards = cardsIn(list);

    list.setAttribute('data-se-stock', 'pending');
    window.setTimeout(function () { release(list); }, FAILSAFE_MS);

    // The page the server just rendered is already in the DOM - no request needed.
    store[current] = liveCards.map(function (c) { return c.outerHTML; });

    loadMissing(store, pages).then(function () {
      if (token !== runId || !document.body.contains(list)) { return; }

      var seen = {};
      var inStock = [];
      var soldOut = [];
      for (var n = 1; n <= pages; n++) {
        (store[n] || []).forEach(function (html) {
          var card = htmlToCard(html);
          if (!card) { return; }
          var id = cardId(card);
          if (seen[id]) { return; }
          seen[id] = true;
          (isAvailable(card) ? inStock : soldOut).push(card);
        });
      }
      var ordered = inStock.concat(soldOut);
      var slice = ordered.slice((current - 1) * size, current * size);
      if (!slice.length) { return; }

      var row = rowOf(list);
      // Reuse the nodes that are already on screen (keeps loaded images) where possible.
      var liveById = {};
      liveCards.forEach(function (c) { liveById[cardId(c)] = c; });
      var frag = document.createDocumentFragment();
      slice.forEach(function (card) {
        frag.appendChild(liveById[cardId(card)] || card);
      });
      while (row.firstChild) { row.removeChild(row.firstChild); }
      row.appendChild(frag);
      list.setAttribute('data-se-stock-done', '1');

      try {
        if (window.vela && typeof window.vela.swatchProduct === 'function') { window.vela.swatchProduct(); }
        if (window.lazySizes && window.lazySizes.autoSizer) { window.lazySizes.autoSizer.checkElems(); }
      } catch (e) { /* cosmetic hooks only */ }
    }).catch(function () {
      /* Any failure leaves the server-rendered page (in-stock first per page) in place. */
    }).then(function () {
      if (token === runId) { release(list); }
    });
  }

  window.SEStockFirst = { run: run };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
